import { ChangeDetectorRef, EnvironmentInjector, HostListener, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService, AuthenticationService, CachedValuesService, ToastService } from '@services/index';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormGroup } from '@angular/forms';
import { AgreementForm } from '../../feature/shared-agreement.model';
import { switchMap, of, interval, takeWhile, take } from 'rxjs';
import { CanComponentDeactivate } from '@guards/can-deactivate.guard';
import { ModalHelper } from '@helpers/modal-helper';
import { ConfirmModal } from '@shared/components/confirm-modal/confirm-modal.model';
import { GenerateMetaDataStatus } from 'app/enums';
import {
    AgreementFilesRequest,
    PeriodRenewalsRequest,
    ReminderRequest,
    PaymentRequest,
    AgreementNoteRemindersRequest,
    AgreementNoteReminderRequest,
    GenerateMetaDataRequest,
    RealEstateRequest,
    RentalRequest,
    InventoryRequest
} from '@services/api-client/requests';
import { AgreementFile } from 'app/types/agreement-file.type';
import { fromNgbDate } from '@helpers/datetime-helper';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AgreementMetadataGenerationService } from '../agreement-metadata-generation.helper';
import {
    ValidationFailure,
    ValidationFailuresModalComponent
} from '../../components/validation-failures-modal/validation-failures-modal.component';
import { RiskAnalysis } from '../../components/risk-analysis/risk-analysis.component';

enum TabIndex {
    SelectType = 0,
    GeneralInfo = 1,
    PeriodsAndRenewals = 2,
    NotesAndActivities = 3,
    Attachments = 4
}

@UntilDestroy()
export class AgreementBaseComponent implements CanComponentDeactivate {
    protected readonly apiClientService = inject(ApiClientService);
    protected readonly translateService = inject(TranslateService);
    protected readonly toastService = inject(ToastService);
    protected readonly router = inject(Router);
    protected readonly route = inject(ActivatedRoute);
    protected readonly injector = inject(EnvironmentInjector);
    protected readonly authenticationService = inject(AuthenticationService);
    protected readonly changeDetectorRef = inject(ChangeDetectorRef);
    protected readonly modalService = inject(NgbModal);
    private readonly cachedValuesService = inject(CachedValuesService);
    protected readonly metadataService = inject(AgreementMetadataGenerationService);

    public isSaving = false;
    public generatedCompletedMetaData = false;
    public tabIndex = 0;
    public tabs = TabIndex;
    private fileDialogOpened = false;
    private fileDialogClosedTime = 0;
    protected inboxId: string;

    public agreementForm: FormGroup<AgreementForm>;

    public get f() {
        return this.agreementForm?.controls;
    }

    public get detailsForm() {
        return this.f?.details;
    }

    public get periodForm() {
        return this.f?.period;
    }

    public get reminderForm() {
        return this.f?.reminder;
    }

    public get paymentForm() {
        return this.f?.payment;
    }

    public get attachmentForm() {
        return this.f?.attachments;
    }

    @HostListener('window:beforeunload', ['$event'])
    public canDeactivate() {
        // Ignore events that occur within 500ms of file dialog closing
        if (this.fileDialogOpened || Date.now() - this.fileDialogClosedTime < 500) {
            return false;
        }

        const showLeavePageWarning = this.shouldShowLeavePageModal();

        const model: ConfirmModal = {
            titleLanguageKey: 'TITLES.UNSAVED_CHANGES',
            bodyLanguageKey: 'TEXTS.CONFIRM_UNSAVED_CHANGES',
            cancelLanguageKey: 'BUTTONS.CANCEL',
            confirmLanguageKey: 'BUTTONS.CONFIRM'
        };

        if (showLeavePageWarning) {
            return this.injector.runInContext(() => ModalHelper.RunInContext.showLeavePageConfirmModal(model));
        }

        return true;
    }

    @HostListener('window:beforeunload', ['$event'])
    public beforeUnload(event: BeforeUnloadEvent) {
        // Ignore events that occur within 500ms of file dialog closing
        if (this.fileDialogOpened || Date.now() - this.fileDialogClosedTime < 500) {
            return;
        }

        const showLeavePageWarning = this.shouldShowLeavePageModal();

        if (showLeavePageWarning) {
            event.returnValue = true;
        }
    }

    private shouldShowLeavePageModal() {
        return this.agreementForm?.dirty;
    }

    public onFileDialogStateChange(isOpen: boolean) {
        this.fileDialogOpened = isOpen;
        if (!isOpen) {
            this.fileDialogClosedTime = Date.now();
        }
    }

    protected storePaymentDetails(agreementId: string) {
        return switchMap(() => {
            if (this.f.payment?.dirty) {
                console.log('Store payment');
                const request: PaymentRequest = {
                    paymentTerms:
                        this.f.payment.value.paymentTerms >= 0
                            ? this.f.payment.value.paymentTerms
                            : this.f.payment.value.customPaymentTerms,
                    value: this.f.payment.value.value ?? 0,
                    currency: this.f.payment.value.currency,
                    invoiceIntervalId: this.f.payment.value.invoiceIntervalId ?? 0
                };
                return this.apiClientService.putAgreementPayment$(agreementId, request);
            }
            return of(null);
        });
    }

    protected storeAttachments(agreementId: string) {
        return switchMap(() => {
            if (this.f.attachments.controls.attachments.dirty) {
                console.log('Store attachments');
                const fileRequest = this.f.attachments.controls.attachments.value.map((file) => {
                    return {
                        id: file.id,
                        url: file.url,
                        fileName: file.fileName,
                        fileExtension: file.fileExtension
                    } as AgreementFile;
                }) as AgreementFilesRequest;
                return this.apiClientService.putAgreementFiles$(agreementId, fileRequest);
                // TODO get file id or reload agreement
            }
            return of(null);
        });
    }

    protected storeAgreementPeriod(agreementId: string) {
        return switchMap(() => {
            if (this.f.period.dirty) {
                console.log('Store period');
                const periodRequest: PeriodRenewalsRequest = {
                    validFrom: fromNgbDate(this.f.period.value.validFrom),
                    validTo: fromNgbDate(this.f.period.value.validTo),
                    terminationDate: fromNgbDate(this.f.period.value.terminationDate),
                    commitmentPeriodTypeId: this.f.period.value.commitmentPeriodTypeId,
                    noticePeriod: this.f.period.value.noticePeriod,
                    noticePeriodTypeId: this.f.period.value.noticePeriodTypeId,
                    periodAfterExtension: this.f.period.value.periodAfterExtension,
                    periodTypeIdAfterExtension: this.f.period.value.periodTypeIdAfterExtension,
                    expiryDateAfterTermination: fromNgbDate(this.f.period.value.expiryDateAfterTermination),
                    noticePeriodAfterExtension: this.f.period.value.noticePeriodAfterExtension,
                    noticePeriodTypeIdAfterExtension: this.f.period.value.noticePeriodTypeIdAfterExtension,
                    managedDate: fromNgbDate(this.f.period.value.managedDate)
                };

                return this.apiClientService.putAgreementPeriodsRenewals$(agreementId, periodRequest);
            }
            return of(null);
        });
    }

    protected storeReminderDetails(agreementId: string) {
        return switchMap(() => {
            if (this.f.reminder.dirty) {
                console.log('Store reminder');
                const request: ReminderRequest = {
                    recipients: this.f.reminder.value.recipients ?? [],
                    reminderPeriod: this.f.reminder.value.reminderPeriod ?? 0,
                    periodTypeId: 1,
                    numOfReminders: this.f.reminder.value.numOfReminders ?? 0,
                    escalatedRecipients: this.f.reminder.value.escalatedRecipients ?? []
                };
                return this.apiClientService.putAgreementReminder$(agreementId, request);
            }
            return of(null);
        });
    }

    protected storeNoteReminders(agreementId: string) {
        return switchMap(() => {
            if (this.f.notesReminders.dirty || this.f.period.controls.managedNote.dirty) {
                console.log('Store note reminder');
                let managedNote: AgreementNoteReminderRequest = null;
                if (this.f.period.controls.managedNote.dirty) {
                    console.log('Store managed note');
                    managedNote = {
                        note: this.f.period.controls.managedNote.value,
                        subject: 'Managed Note',
                        reminder: false,
                        reminderTime: '',
                        to: []
                    } as AgreementNoteReminderRequest;
                }
                const request = this.f.notesReminders.controls.notes.value.map((item) => {
                    return {
                        id: item.id,
                        note: item.note,
                        reminder: item.reminder,
                        subject: item.subject,
                        to: item.id ? [] : item.to,
                        reminderDate: item.reminderDate
                        //reminderTime: item.reminderTime
                    } as AgreementNoteReminderRequest;
                }) as AgreementNoteRemindersRequest;
                if (managedNote) {
                    request.push(managedNote);
                }
                return this.apiClientService.putAgreementNoteReminders$(agreementId, request);
            }
            return of(null);
        });
    }

    protected storeInventoryDetails(agreementId: string) {
        return switchMap(() => {
            if (this.f.inventory?.dirty) {
                console.log('Store inventory');
                const request: InventoryRequest = {
                    inventoryNumber: this.f.inventory.value.inventoryNumber,
                    typeOfInventory: this.f.inventory.value.typeOfInventory,
                    condition: this.f.inventory.value.condition,
                    serialNumber: this.f.inventory.value.serialNumber,
                    location: this.f.inventory.value.location,
                    benefitValue: this.f.inventory.value.benefitValue,
                    warranty: this.f.inventory.value.warranty,
                    manufacturer: this.f.inventory.value.manufacturer,
                    model: this.f.inventory.value.model,
                    modelYear: this.f.inventory.value.modelYear,
                    user: this.f.inventory.value.user,
                    purchaseCost: this.f.inventory.value.purchaseCost
                };
                return this.apiClientService.putAgreementInventory$(agreementId, request);
            }
            return of(null);
        });
    }

    protected storeRentalDetails(agreementId: string) {
        return switchMap(() => {
            if (this.f.rental?.dirty) {
                console.log('Store rental');
                const request: RentalRequest = {
                    employeeNo: this.f.rental.value.employeeNo,
                    employeeName: this.f.rental.value.employeeName,
                    grossSalaryDeduction: this.f.rental.value.grossSalaryDeduction,
                    benefitValue: this.f.rental.value.benefitValue,
                    additionalInsurance: this.f.rental.value.additionalInsurance,
                    registrationNo: this.f.rental.value.registrationNo,
                    carBrand: this.f.rental.value.carBrand,
                    carModel: this.f.rental.value.carModel,
                    modelYear: this.f.rental.value.modelYear,
                    mileageAnnual: this.f.rental.value.mileageAnnual
                };
                return this.apiClientService.putAgreementRental$(agreementId, request);
            }
            return of(null);
        });
    }

    protected storeRealEstateDetails(agreementId: string) {
        return switchMap(() => {
            if (this.f.realEstate?.dirty) {
                console.log('Store real estate');
                const request: RealEstateRequest = {
                    propertyNo: this.f.realEstate.value.propertyNo,
                    apartmentNo: this.f.realEstate.value.apartmentNo,
                    size: this.f.realEstate.value.size,
                    typeOfPremises: this.f.realEstate.value.typeOfPremises
                };
                return this.apiClientService.putAgreementRealEstate$(agreementId, request);
            }
            return of(null);
        });
    }

    protected get hasAITokens() {
        let hasTokens = false;
        this.cachedValuesService.subscription$.subscribe((response) => {
            hasTokens = response?.numOfCredits > response?.usedCredits && response?.ai;
        });
        return hasTokens;
    }

    protected get hasRiskAnalysis() {
        if (!this.hasAITokens) return false;
        let riskAnalysis = false;
        this.cachedValuesService.subscription$.subscribe((response) => {
            riskAnalysis = response?.riskAnalysis;
        });
        return riskAnalysis;
    }

    public get hasNewPdf() {
        return !!this.f.fileUpload?.value?.file?.url;
    }

    public get hasExistingPdf() {
        return !!this.f.fileUpload?.value?.file?.id;
    }

    private get isInventoryAgreement(): boolean {
        return this.f.details?.controls.agreementTypeId?.value === 13; // 13 = Inventory
    }

    public get canGenerateMetaData() {
        return (
            (this.hasNewPdf || this.hasExistingPdf) && !this.generatedCompletedMetaData && !this.isInventoryAgreement
        );
    }

    public get canGenerateRiskAnalysis() {
        return !this.f.details.controls.riskAnalysis.value && (this.hasNewPdf || this.hasExistingPdf);
    }

    public getGenerateMetaDataTooltip() {
        if (!this.hasNewPdf && !this.hasExistingPdf) {
            return this.translateService.instant('TEXTS.UPLOAD_PDF_TO_GENERATE_METADATA') as string;
        }
        if (!this.hasAITokens) {
            return this.translateService.instant('TEXTS.NOT_ENOUGH_AI_TOKENS_TO_GENERATE_METADATA') as string;
        }
        return '';
    }

    protected checkAgreementFileChanged() {
        this.f.fileUpload.controls.file.valueChanges.pipe(untilDestroyed(this)).subscribe((file) => {
            if (file?.fileName) {
                this.generatedCompletedMetaData = false;

                // Skip popup if file is from inbox and hasn't been processed yet
                // (will show on next() click instead)
                if (this.inboxId) {
                    return;
                }

                // Skip popup if agreement type is inventory
                if (this.isInventoryAgreement) {
                    return;
                }

                // Show confirmation popup for manually uploaded files if user has tokens
                this.showMetadataGenerationConfirmation();
            }
        });
    }

    protected showMetadataPopupIfEligible(): void {
        const fileValue = this.f.fileUpload?.value?.file;

        // Show metadata generation confirmation popup if file is available and user has tokens
        // Skip if agreement type is inventory
        if (fileValue && !this.isInventoryAgreement) {
            this.showMetadataGenerationConfirmation();
        }
    }

    private showMetadataGenerationConfirmation(): void {
        this.cachedValuesService.subscription$.pipe(take(1), untilDestroyed(this)).subscribe((response) => {
            if (response?.numOfCredits > response?.usedCredits && (response?.ai || response?.riskAnalysis)) {
                const modalRef = this.modalService.open(ConfirmModalComponent, {
                    backdrop: 'static'
                });
                const modalInstanceHelper = ModalHelper.getConfirmModalInstanceHelper(modalRef);

                modalInstanceHelper.componentInstance.model = {
                    titleLanguageKey: 'TITLES.SEND_FOR_METADATA_GENERATION_OF_AGREEMENT',
                    bodyLanguageKey: 'TEXTS.CONFIRM_SEND_FOR_METADATA_GENERATION_OF_AGREEMENT',
                    cancelLanguageKey: 'BUTTONS.NO',
                    confirmLanguageKey: 'BUTTONS.YES'
                };

                modalInstanceHelper.confirmation$
                    .pipe(
                        untilDestroyed(this),
                        switchMap((confirm) => {
                            if (confirm) {
                                this.changeDetectorRef.markForCheck();
                                this.generateMetaData(null, response.ai, response.riskAnalysis);
                            }
                            return of(null);
                        })
                    )
                    .subscribe({
                        next: () => {
                            // const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                            // const message = this.translateService.instant(
                            //     'FEATURES.AGREEMENTS.TOAST_SENT_TO_AI_SUCCESS'
                            // ) as string;
                            // this.toastService.open(title, message, { type: 'success', progressBar: true });
                        }
                    });
            }
        });
    }

    public generatingMetaData = false;
    private processingToastId: number | null = null;

    public generateMetaData(
        event: Event,
        generateMetaData: boolean,
        generateRiskAnalysis: boolean = this.hasRiskAnalysis
    ) {
        event?.stopPropagation();
        this.generatingMetaData = true;
        console.log('[AgreementBase] Starting metadata generation...');
        const request: GenerateMetaDataRequest = {
            filename: this.f.fileUpload?.value?.file?.url,
            fileId: this.f.fileUpload?.value?.file?.id,
            generateMetaData: generateMetaData,
            generateRiskAnalysis: generateRiskAnalysis
        };

        // Show initial processing toast
        const processingTitle = this.translateService.instant('TOAST_TITLES.INFO') as string;
        const processingMessage = this.translateService.instant(
            'FEATURES.AGREEMENTS.TOAST_GENERATING_METADATA.MESSAGE'
        ) as string;
        this.processingToastId = this.toastService.open(processingTitle, processingMessage, {
            type: 'info',
            spinner: true,
            dismissable: true
        });

        this.apiClientService
            .generateMetaData$(request)
            .pipe(
                switchMap((response) => of(response)),
                switchMap((response) =>
                    interval(5000).pipe(
                        switchMap(() => this.apiClientService.getMetaData$(response.id)),
                        takeWhile(
                            (meta) =>
                                meta.generateMetaDataStatusId !== GenerateMetaDataStatus.Completed &&
                                meta.generateMetaDataStatusId !== GenerateMetaDataStatus.Failed,
                            true
                        )
                    )
                )
            )
            .subscribe({
                next: (meta) => {
                    if (meta.generateMetaDataStatusId === GenerateMetaDataStatus.Completed) {
                        console.log('[AgreementBase] Metadata generation completed');
                        this.generatingMetaData = false;
                        this.generatedCompletedMetaData = true;

                        // Dismiss the processing toast
                        if (this.processingToastId !== null) {
                            this.toastService.dismiss(this.processingToastId);
                            this.processingToastId = null;
                        }

                        const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                        const message = this.translateService.instant(
                            'FEATURES.AGREEMENTS.TOAST_METADATA_GENERATED_SUCCESS.MESSAGE'
                        ) as string;
                        this.toastService.open(title, message, { type: 'success', progressBar: true });

                        void this.metadataService
                            .setFormValuesFromMetadataList(this.agreementForm.controls, meta.metaDataFields)
                            .then((result) => {
                                // Handle failed counterpart names
                                if (result.failedCounterpartNames && result.failedCounterpartNames.length > 0) {
                                    console.log(
                                        '[AgreementBase] Failed counterparts detected:',
                                        result.failedCounterpartNames
                                    );
                                    // Call handler method if available (overridden in child classes)
                                    this.handleFailedCounterparts(result.failedCounterpartNames);
                                    this.changeDetectorRef.markForCheck();
                                }

                                if (
                                    (result.validationFailures && result.validationFailures.length > 0) ||
                                    result.riskAnalysis
                                ) {
                                    this.f.details.controls.riskAnalysis.setValue(JSON.stringify(result.riskAnalysis));
                                    this.f.details.controls.riskAnalysis.markAsDirty();
                                    this.showValidationFailuresAndRiskAnalysisModal(
                                        result.validationFailures,
                                        result.riskAnalysis
                                    );
                                }
                                this.changeDetectorRef.markForCheck();
                                this.changeDetectorRef.detectChanges();
                            });
                    } else if (meta.generateMetaDataStatusId === GenerateMetaDataStatus.Failed) {
                        console.error('[AgreementBase] Metadata generation failed');
                        this.generatingMetaData = false;

                        // Dismiss the processing toast
                        if (this.processingToastId !== null) {
                            this.toastService.dismiss(this.processingToastId);
                            this.processingToastId = null;
                        }

                        const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                        const message = this.translateService.instant(
                            'FEATURES.AGREEMENTS.TOAST_SENT_TO_AI_ERROR.MESSAGE'
                        ) as string;

                        this.toastService.open(title, message, { type: 'error', progressBar: true });
                        this.changeDetectorRef.detectChanges();
                    }
                },
                error: () => {
                    console.error('[AgreementBase] Error during metadata generation');
                    this.generatingMetaData = false;

                    // Dismiss the processing toast
                    if (this.processingToastId !== null) {
                        this.toastService.dismiss(this.processingToastId);
                        this.processingToastId = null;
                    }

                    this.changeDetectorRef.detectChanges();
                }
            });
    }

    // Define a proper type for riskAnalysis, replace 'RiskAnalysisType' with the actual type if available
    private showValidationFailuresAndRiskAnalysisModal(
        validationFailures: ValidationFailure[] | null,
        riskAnalysis: RiskAnalysis | null
    ): void {
        const modalRef = this.modalService.open(ValidationFailuresModalComponent, {
            size: 'xl'
        });
        const modalInstance = ModalHelper.getComponentInstance<ValidationFailuresModalComponent>(modalRef);
        modalInstance.validationFailures = validationFailures || [];
        modalInstance.riskAnalysis = riskAnalysis;
    }

    protected handleFailedCounterparts(failedCounterpartNames: string[]): void {
        // This method can be overridden in child classes to handle failed counterparts
        // Parameter: failedCounterpartNames - list of counterpart names that were not found
        // Default implementation stores the first counterpart name in the metadata service
        if (failedCounterpartNames && failedCounterpartNames.length > 0) {
            console.log('[AgreementBase] Handling failed counterpart:', failedCounterpartNames[0]);
            this.metadataService.setPendingCounterpartName(failedCounterpartNames[0]);
        }
    }
}
